import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Alert, Badge, Button, Card, Group, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconLock, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { fetchRole, fetchRoleClaims, addRoleClaim, removeRoleClaim } from '@/features/roles/api';
import { PageLoader } from '@/shared/components/LoadingState';
import { MaePageHead } from '@/shared/desktop/MaeShell';

export const Route = createFileRoute('/_app/roles/$roleId')({ component: RoleDetailPage });

function RoleDetailPage() {
  const { roleId } = useParams({ from: '/_app/roles/$roleId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [newClaim, setNewClaim] = useState({ type: 'custom', value: '' });

  const { data: role } = useQuery({ queryKey: ['role', roleId], queryFn: () => fetchRole(roleId) });

  const { data: claims, isLoading: cLoading, isError: cError, error: cErr, refetch: cRefetch } = useQuery({
    queryKey: ['role-claims', roleId],
    queryFn: () => fetchRoleClaims(roleId),
  });

  const add = useMutation({
    mutationFn: () => addRoleClaim(roleId, newClaim.type, newClaim.value),
    onSuccess: () => {
      setNewClaim({ type: 'custom', value: '' });
      qc.invalidateQueries({ queryKey: ['role-claims', roleId] });
      notifications.show({ color: 'green', message: 'Claim aggiunto' });
    },
    onError: (err) => notifyError(err),
  });
  const remove = useMutation({
    mutationFn: removeRoleClaim,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['role-claims', roleId] });
      notifications.show({ color: 'green', message: 'Claim rimosso' });
    },
  });

  // Raggruppa claim per "entità" stimata (prima parte di claimValue, tipo "Project.create" → "Project")
  const grouped = useMemo(() => {
    const out: Record<string, NonNullable<typeof claims>['data']> = {};
    (claims?.data ?? []).forEach((c) => {
      const subject = c.claimValue?.split('.')[0] ?? c.claimType ?? 'altro';
      (out[subject] ??= []).push(c);
    });
    return out;
  }, [claims]);

  if (!role) return <PageLoader label="Carico il ruolo…" />;

  // BE (post-v1.9): i ruoli di sistema (master) sono read-only — claim non
  // modificabili (POST/DELETE role-claims → 403). Nascondiamo le azioni di scrittura.
  const isMaster = role.isFromMaster === true;

  return (
    <Stack>
      {/* BE v1.20.22: sui profili ridotti `description` porta le note di capability */}
      <MaePageHead
        onBack={() => void navigate({ to: '/roles' })}
        backLabel="Torna ai Ruoli"
        title={role.name ?? 'Ruolo'}
        sub={role.description || undefined}
        badges={isMaster ? <Badge color="gray" variant="light" leftSection={<IconLock size={12} />}>Ruolo di sistema</Badge> : undefined}
      />

      {isMaster && (
        <Alert color="gray" variant="light" icon={<IconLock size={18} />}>
          Questo è un ruolo di sistema ereditato: i suoi permessi sono gestiti centralmente e non possono essere
          modificati. Puoi comunque assegnarlo agli utenti.
        </Alert>
      )}

      {!isMaster && (
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Title order={3}>Aggiungi claim</Title>
          <Group>
            <TextInput
              placeholder="Tipo (es. custom)"
              value={newClaim.type}
              onChange={(e) => setNewClaim({ ...newClaim, type: e.currentTarget.value })}
              w={140}
            />
            <TextInput
              placeholder="Valore (es. Project.create)"
              value={newClaim.value}
              onChange={(e) => setNewClaim({ ...newClaim, value: e.currentTarget.value })}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              disabled={!newClaim.value}
              loading={add.isPending}
              onClick={() => void add.mutate()}
            >
              Aggiungi
            </Button>
          </Group>
        </Stack>
      </Card>
      )}

      {cError && <QueryErrorAlert error={cErr} title="Impossibile leggere i permessi del ruolo" onRetry={() => void cRefetch()} />}
      {cLoading && <Loader />}

      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([subject, rows]) => (
          <Card key={subject} withBorder padding="lg" radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Title order={4}>{subject}</Title>
                <Badge>{rows.length}</Badge>
              </Group>
              <Stack gap={4}>
                {rows.map((c) => (
                  <Group key={c.id} justify="space-between">
                    <Group gap="xs">
                      <Badge variant="light">{c.claimType}</Badge>
                      <Text size="sm">{c.claimValue}</Text>
                    </Group>
                    {!isMaster && (
                      <Button
                        variant="subtle"
                        color="red"
                        size="xs"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => void remove.mutate(c.id)}
                      >
                        Rimuovi
                      </Button>
                    )}
                  </Group>
                ))}
              </Stack>
            </Stack>
          </Card>
        ))}
    </Stack>
  );
}
